import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import { Helmet } from 'react-helmet-async';
import { blogPosts as blogs, siteConfig } from '@/data/siteData';
import { 
  Calendar,
  Clock,
  User,
  ArrowLeft,
  ArrowRight,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  ChevronRight
} from 'lucide-react';

export const BlogDetailPage = () => {
  const { blogId } = useParams();
  const blog = blogs.find(b => b.id === blogId);
  const currentIndex = blogs.findIndex(b => b.id === blogId);
  const prevBlog = currentIndex > 0 ? blogs[currentIndex - 1] : null;
  const nextBlog = currentIndex < blogs.length - 1 ? blogs[currentIndex + 1] : null;
  const relatedBlogs = blogs.filter(b => b.category === blog?.category && b.id !== blogId).slice(0, 3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [blogId]);

  if (!blog) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
            <Link to="/blog">
              <Button>Back to Blog</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.title} - Felix Infotech Blog</title>
        <meta name="description" content={blog.excerpt} />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative py-32 bg-foreground overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={blog.image}
              alt={blog.title}
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground/80 via-foreground/90 to-foreground" />
          </div>
          
          <div className="container mx-auto px-4 lg:px-6 relative z-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-white/60 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/blog" className="hover:text-white">Blog</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">{blog.category}</span>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-primary-foreground max-w-4xl"
            >
              <Badge className="mb-4 bg-white/10 text-white border-white/20">{blog.category}</Badge>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                {blog.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-6 text-white/80">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </div>
                  <span>{blog.author}</span>
                </div>
                <span className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {blog.date}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {blog.readTime} read
                </span>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 100" fill="none" className="w-full">
              <path d="M0 100V40C360 80 720 100 1080 60C1260 40 1380 30 1440 30V100H0Z" fill="hsl(var(--background))" />
            </svg>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-6">
            <div className="grid lg:grid-cols-4 gap-12">
              {/* Main Content */}
              <motion.article
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="lg:col-span-3"
              >
                <div className="prose prose-lg max-w-none">
                  {blog.content.split('\n\n').map((paragraph, index) => {
                    if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      return (
                        <h2 key={index} className="font-display text-2xl font-bold mt-8 mb-4">
                          {paragraph.replace(/\*\*/g, '')}
                        </h2>
                      );
                    }
                    if (paragraph.startsWith('- ')) {
                      const items = paragraph.split('\n').filter(line => line.startsWith('- '));
                      return (
                        <ul key={index} className="list-disc pl-6 space-y-2 my-4">
                          {items.map((item, i) => (
                            <li key={i} className="text-muted-foreground">{item.replace('- ', '')}</li>
                          ))}
                        </ul>
                      );
                    }
                    if (paragraph.match(/^\d+\./)) {
                      const items = paragraph.split('\n').filter(line => line.match(/^\d+\./));
                      return (
                        <ol key={index} className="list-decimal pl-6 space-y-2 my-4">
                          {items.map((item, i) => (
                            <li key={i} className="text-muted-foreground">{item.replace(/^\d+\.\s*/, '')}</li>
                          ))}
                        </ol>
                      );
                    }
                    return (
                      <p key={index} className="text-muted-foreground leading-relaxed my-4">
                        {paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split('<strong>').map((part, i) => {
                          if (part.includes('</strong>')) {
                            const [bold, rest] = part.split('</strong>');
                            return <span key={i}><strong className="text-foreground">{bold}</strong>{rest}</span>;
                          }
                          return part;
                        })}
                      </p>
                    );
                  })}
                </div>

                {/* Share Section */}
                <div className="mt-12 pt-8 border-t border-border">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <Share2 className="w-4 h-4" /> Share:
                      </span>
                      <div className="flex gap-2">
                        <a href="#" className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors">
                          <Facebook className="w-5 h-5" />
                        </a>
                        <a href="#" className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors">
                          <Twitter className="w-5 h-5" />
                        </a>
                        <a href="#" className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors">
                          <Linkedin className="w-5 h-5" />
                        </a>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-primary border-primary">
                      {blog.category}
                    </Badge>
                  </div>
                </div>

                {/* Navigation */}
                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  {prevBlog && (
                    <Link to={`/blog/${prevBlog.id}`} className="group">
                      <div className="p-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors">
                        <span className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                          <ArrowLeft className="w-4 h-4" /> Previous
                        </span>
                        <p className="font-medium line-clamp-1 group-hover:text-primary transition-colors">
                          {prevBlog.title}
                        </p>
                      </div>
                    </Link>
                  )}
                  {nextBlog && (
                    <Link to={`/blog/${nextBlog.id}`} className="group sm:text-right">
                      <div className="p-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors">
                        <span className="text-sm text-muted-foreground flex items-center gap-1 mb-2 sm:justify-end">
                          Next <ArrowRight className="w-4 h-4" />
                        </span>
                        <p className="font-medium line-clamp-1 group-hover:text-primary transition-colors">
                          {nextBlog.title}
                        </p>
                      </div>
                    </Link>
                  )}
                </div>
              </motion.article>

              {/* Sidebar */}
              <aside className="space-y-8">
                {/* CTA */}
                <div className="bg-gradient-primary rounded-2xl p-6 text-white">
                  <h3 className="font-display text-xl font-bold mb-3">Need Expert Help?</h3>
                  <p className="text-sm opacity-90 mb-4">
                    Our team is ready to help you implement these solutions.
                  </p>
                  <Link to="/contact">
                    <Button className="w-full bg-white text-primary hover:bg-white/90">
                      Contact Us
                    </Button>
                  </Link>
                </div>

                {/* Related Articles */}
                {relatedBlogs.length > 0 && (
                  <div className="bg-card rounded-2xl p-6 border border-border">
                    <h3 className="font-display font-semibold mb-4">Related Articles</h3>
                    <div className="space-y-4">
                      {relatedBlogs.map((related) => (
                        <Link key={related.id} to={`/blog/${related.id}`} className="group block">
                          <div className="flex gap-3">
                            <img
                              src={related.image}
                              alt={related.title}
                              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                            />
                            <div>
                              <p className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                                {related.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">{related.readTime}</p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                <div className="bg-card rounded-2xl p-6 border border-border">
                  <h3 className="font-display font-semibold mb-4">Get in Touch</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Have questions about this article or need help?
                  </p>
                  <div className="space-y-2 text-sm">
                    <p><strong>Phone:</strong> {siteConfig.phone}</p>
                    <p><strong>Email:</strong> {siteConfig.email}</p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <ContactForm variant="simple" />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default BlogDetailPage;
